import React, {useContext, useEffect, useState} from "react"
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography
} from "@mui/material"
import {LoadingButton} from "@mui/lab"
import {ArrowForward} from "@mui/icons-material"
import {parse, stringify} from "lossless-json"

import SimpleTable from "../../../components/Common/Elements"
import {alertMsgProcessor} from "../../../core/utils/constants"
import {SnackBarContext} from "../../../snackBarProvider"
import {accountInfoExtractors} from "./accountInfoExtractors"

import {useGetCmProgressQuery, usePutCmProgressMutation} from "../../../core/api/cm-progress-api"
import {useGetStateV2CMQuery, useCreateStateV2CMMutation} from "../../../core/api/cloclmaker-state-api"
import {useGetStateV2DSQuery, useCreateStateV2DSMutation} from "../../../core/api/ds-state-api"
import {useGetStateV2TOHQuery, useCreateStateV2TOHMutation} from "../../../core/api/toh-state-api"
import {useGetStateV2MCQuery, useCreateStateV2MCMutation} from "../../../core/api/mc-state-api"

// Games supported by "Copy to" - the backend whitelist-bypass + audit log is
// only wired up for these controllers so far. Add a new game by:
// 1. adding its account info extractor in accountInfoExtractors.js
// 2. importing its useGetStateV2*Query / useCreateStateV2*Mutation below
// 3. adding a case to the two switch statements in this file
export const COPY_STATE_SUPPORTED_GAMES = ["cm", "ds", "toh", "mc"]

const parseStateData = (obj) => {
    if (!obj || typeof obj !== "object" || !obj.hasOwnProperty("data")) return null
    try {
        return typeof obj.data === "string" ? JSON.parse(obj.data) : obj.data
    } catch (e) {
        return null
    }
}

const AccountInfoCard = ({title, rows}) => (
    <Card variant="outlined">
        <CardContent>
            <Typography sx={{fontSize: 16}}>{title}</Typography>
        </CardContent>
        <SimpleTable data={rows || []}/>
    </Card>
)

const CopyStateDialog = ({open, onClose, game, sourceDeviceId, sourceVendorId, rawJson, onSuccess, api = "v2"}) => {
    const isProgress = api === "progress"
    const [targetStateId, setTargetStateId] = useState("")
    const [targetVendorId, setTargetVendorId] = useState("")
    const [comment, setComment] = useState("")
    const [submittedQuery, setSubmittedQuery] = useState(null)

    const useSnackBars = () => useContext(SnackBarContext)
    const {addAlert} = useSnackBars()

    useEffect(() => {
        if (!open) {
            setTargetStateId("")
            setTargetVendorId("")
            setComment("")
            setSubmittedQuery(null)
        }
    }, [open])

    const cmQuery = useGetStateV2CMQuery(
        {deviceId: submittedQuery?.deviceId, stateId: submittedQuery?.stateId, version: "v2"},
        {skip: !submittedQuery || game !== "cm" || isProgress}
    )
    const progressQuery = useGetCmProgressQuery(
        submittedQuery?.deviceId,
        {skip: !submittedQuery || !isProgress}
    )
    const dsQuery = useGetStateV2DSQuery(
        {deviceId: submittedQuery?.deviceId, stateId: submittedQuery?.stateId},
        {skip: !submittedQuery || game !== "ds"}
    )
    const tohQuery = useGetStateV2TOHQuery(
        {deviceId: submittedQuery?.deviceId, stateId: submittedQuery?.stateId},
        {skip: !submittedQuery || game !== "toh"}
    )
    const mcQuery = useGetStateV2MCQuery(
        {deviceId: submittedQuery?.deviceId, stateId: submittedQuery?.stateId},
        {skip: !submittedQuery || game !== "mc"}
    )

    const [createCMState, createCMStateResponse] = useCreateStateV2CMMutation()
    const [putCmProgress, putCmProgressResponse] = usePutCmProgressMutation()
    const [createDSState, createDSStateResponse] = useCreateStateV2DSMutation()
    const [createTOHState, createTOHStateResponse] = useCreateStateV2TOHMutation()
    const [createMCState, createMCStateResponse] = useCreateStateV2MCMutation()

    const targetQuery = isProgress ? progressQuery : ({cm: cmQuery, ds: dsQuery, toh: tohQuery, mc: mcQuery}[game] || {})
    const isConfirmLoading = createCMStateResponse.isLoading
        || putCmProgressResponse.isLoading
        || createDSStateResponse.isLoading
        || createTOHStateResponse.isLoading
        || createMCStateResponse.isLoading

    const handleSearch = () => {
        const trimmedStateId = targetStateId.trim()
        if (!trimmedStateId) return
        setSubmittedQuery({
            deviceId: trimmedStateId,
            stateId: (targetVendorId.trim() || trimmedStateId)
        })
    }

    const handleChangeTarget = () => {
        setSubmittedQuery(null)
    }

    let fromRows = null
    let fromError = false
    try {
        fromRows = accountInfoExtractors[game](JSON.parse(rawJson))
    } catch (e) {
        fromError = true
    }

    const targetData = parseStateData(targetQuery.data)
    const toRows = targetData ? accountInfoExtractors[game](targetData) : null

    const handleConfirm = async () => {
        let dataString
        try {
            dataString = stringify(parse(rawJson))
        } catch (e) {
            dataString = rawJson
        }

        const finalVendorId = targetVendorId.trim() || targetStateId.trim()

        const data = {
            userId: targetStateId.trim(),
            vendorId: finalVendorId,
            game,
            body: dataString,
            version: "v2",
            isDirectCopy: true,
            sourceUserId: sourceDeviceId,
            sourceVendorId: sourceVendorId,
            comment: comment.trim() || undefined
        }

        let response = false
        if (isProgress) {
            response = await putCmProgress({userId: targetStateId.trim(), body: dataString})
        } else {
            switch (game) {
                case "cm":
                    response = await createCMState(data)
                    break
                case "ds":
                    response = await createDSState(data)
                    break
                case "toh":
                    response = await createTOHState(data)
                    break
                case "mc":
                    response = await createMCState(data)
                    break
                default:
                    console.error("game not supported for copy state process")
            }
        }

        alertMsgProcessor(response, addAlert)

        if (response && !response.error) {
            onSuccess?.()
            onClose()
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isProgress ? "Copy progress to another id" : "Copy state to another id"}</DialogTitle>
            <DialogContent>
                {!submittedQuery && (
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={isProgress ? "User ID" : "State id"}
                            required
                            value={targetStateId}
                            onChange={(e) => setTargetStateId(e.target.value)}
                            fullWidth
                        />
                        {!isProgress &&
                            <TextField
                                label="Vendor id"
                                helperText="Optional, falls back to State id"
                                value={targetVendorId}
                                onChange={(e) => setTargetVendorId(e.target.value)}
                                fullWidth
                            />
                        }
                        {!isProgress &&
                            <TextField
                                label="Comment"
                                helperText="Optional, will be saved with the copy log"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                            />
                        }
                    </Stack>
                )}

                {submittedQuery && (targetQuery.isLoading || targetQuery.isFetching) && (
                    <Box sx={{display: "flex", justifyContent: "center", p: 4}}>
                        <CircularProgress/>
                    </Box>
                )}

                {submittedQuery && !targetQuery.isLoading && !targetQuery.isFetching && targetQuery.isError && (
                    <Stack spacing={2} sx={{mt: 1}}>
                        <Alert severity="error">
                            {isProgress
                                ? `Progress not found for User ID "${submittedQuery.deviceId}"`
                                : `State not found for State id "${submittedQuery.deviceId}" / Vendor id "${submittedQuery.stateId}"`
                            }
                        </Alert>
                        <Button onClick={handleChangeTarget} variant="outlined" >
                            Back
                        </Button>
                    </Stack>
                )}

                {submittedQuery && toRows && (
                    <Stack spacing={2} sx={{mt: 1}}>
                        {fromError
                            ? <Alert severity="error">Can't parse current state JSON</Alert>
                            : <Alert severity="warning">
                                You are about to replace the state on the right with the state on the left.
                            </Alert>
                        }
                        <Grid container spacing={2} sx={{alignItems: "center"}}>
                            <Grid size={5}>
                                <Typography variant="caption" color="text.secondary">
                                    From: {sourceDeviceId}{sourceVendorId ? ` / ${sourceVendorId}` : ""}
                                </Typography>
                                <AccountInfoCard title="Account info" rows={fromRows}/>
                            </Grid>
                            <Grid size={2} sx={{display: "flex", justifyContent: "center"}}>
                                <ArrowForward fontSize="large"/>
                            </Grid>
                            <Grid size={5}>
                                <Typography variant="caption" color="text.secondary">
                                    To: {isProgress ? submittedQuery.deviceId : `${submittedQuery.deviceId} / ${submittedQuery.stateId}`}
                                </Typography>
                                <AccountInfoCard title="Account info" rows={toRows}/>
                            </Grid>
                        </Grid>
                        <Button onClick={handleChangeTarget} variant="outlined"  sx={{alignSelf: "flex-start"}}>
                            Change target
                        </Button>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button  onClick={onClose}>
                    Cancel
                </Button>
                {!submittedQuery && (
                    <Button variant="contained" onClick={handleSearch} disabled={!targetStateId.trim()}>
                        Search
                    </Button>
                )}
                {submittedQuery && toRows && (
                    <LoadingButton
                        variant="contained"
                        color="warning"
                        loading={isConfirmLoading}
                        disabled={fromError}
                        onClick={handleConfirm}
                    >
                        Confirm
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default CopyStateDialog
